
import { Component, Vue } from 'vue-property-decorator';
import UploadButton from '@/components/UploadButton.vue';
import { dispatchGetFinance, dispatchSetupComplete, dispatchSetUpFinance } from '@/store/setup/actions';
import { IFinance, ISetUpFinance } from '@/store/setup';
import { IMaterial } from '@/interfaces';
import { dispatchGetFile, dispatchGetFinanceMaterial } from '@/store/report/actions';
import { commitAddNotification } from '@/store/main/mutations';
import { sizeLimit, thousand } from '@/utils/const';

@Component({
  components: {
    UploadButton,
  },
})
export default class Finance extends Vue {
  public isClick = false;
  public fileClear = false;
  public materials: IMaterial[] = [];
  public deleteFileIds: number[] = [];
  public user_need_consultation: boolean | null = null;

  public finance_expect_finance_amount: number | null = null;
  public finance_use_of_funds: string[] = [];
  public finance_expect_finance_method: string[] = [];
  public finance_expect_valuation_pre: number | null = null;
  public finance_achievement: string = '';
  public finance_materials: File[] = [];
  public material_error_messages: string[] = [];

  public use_of_funds: string[] = ["人件費", "採用費", "外注費・業務委託費", "広告宣伝費", "研究開発費", "借入金返済費", "その他"];
  public selected_other = false;
  public use_of_fund_other: string = ""

  public year: number | null = null;
  public month: number | null = null;
  public day: number | null = null;

  public async mounted() {
    this.reset();
  }

  public async reset() {
    this.materials = [];
    this.finance_materials = [];
    this.deleteFileIds = [];
    this.material_error_messages = [];

    const finance = await dispatchGetFinance(this.$store);
    if (finance) {
      if (finance.finance_expect_finance_date) {
        let date = new Date(finance.finance_expect_finance_date);
        this.year = date.getFullYear();
        this.month = date.getMonth() + 1;
        this.day = date.getDate();
        this.isClick = true;
      }
      if (finance.finance_expect_finance_amount) {
        this.finance_expect_finance_amount = finance.finance_expect_finance_amount / thousand;
        this.isClick = true;
      }
      if (finance.finance_expect_finance_method && finance.finance_expect_finance_method.length > 0) {
        this.finance_expect_finance_method = Array.from(finance.finance_expect_finance_method);
        this.isClick = true;
      }
      if (finance.finance_expect_valuation_pre) {
        this.finance_expect_valuation_pre = finance.finance_expect_valuation_pre / thousand;
        this.isClick = true;
      }
      if (finance.finance_use_of_funds && finance.finance_use_of_funds.length > 0) {
        this.finance_use_of_funds = Array.from(finance.finance_use_of_funds);
        for (const [index, finance_use_of_fund] of this.finance_use_of_funds.entries()) {
          if (!this.use_of_funds.includes(finance_use_of_fund)) {
            this.use_of_fund_other = this.finance_use_of_funds[index];
            this.finance_use_of_funds[index] = 'その他';
            break;
          }
        }
        this.isClick = true;
      }
      if (finance.finance_achievement) {
        this.finance_achievement = finance.finance_achievement;
        this.isClick = true;
      }
      if (finance.finance_materials.length > 0) {
        this.materials = Array.from(finance.finance_materials);
        this.isClick = true;
      }
      if (finance.user_need_consultation != null) {
        this.user_need_consultation = finance.user_need_consultation;
      }
    }
    this.$validator.reset();
  }

  public resetItems() {
    if (!this.isClick) {
      this.finance_expect_finance_amount = null;
      this.finance_use_of_funds = [];
      this.use_of_fund_other = '';
      this.finance_expect_finance_method = [];
      this.finance_expect_valuation_pre = null;
      this.finance_achievement = '';
      this.finance_materials = [];
      this.deleteFileIds = [];
      for (let material of this.materials) {
        this.deleteFileIds.push(material.id);
      }
      this.materials = [];
      this.year = 0;
      this.month = 0;
      this.day = 0;
    }
  }

  public resetOther() {
    if (!this.finance_use_of_funds.includes('その他')) {
      this.use_of_fund_other = '';
    }
  }

  public back() {
    this.$router.back();
  }

  public async download(id: number, file_name: string) {
    await dispatchGetFinanceMaterial(this.$store, { "id": id, "file_name": file_name });
  }

  public getFile(file: File, file_name: string) {
    dispatchGetFile(this.$store, { file: file, file_name: file_name });
  }

  public uploadFiles(value) {
    this.finance_materials.push(value[0]);
    this.fileClear = true;
    this.$nextTick(function () {
      this.fileClear = false;
    });
  }

  public deleteFile(uploaded: boolean, index: number) {
    if (uploaded) {
      this.deleteFileIds.push(this.materials[index].id);
      this.materials.splice(index, 1);
    } else {
      this.finance_materials.splice(index, 1);
    }
  }

  public checkFileSize(): boolean {
    let bool = true;
    this.material_error_messages = [];
    for (let i = 0; i < this.finance_materials.length; i++) {
      if (this.finance_materials[i].size > sizeLimit) {
        this.material_error_messages.push(`ファイルサイズは100MB以下にしてください`);
        bool = false;
      } else {
        this.material_error_messages.push('');
      }
    }
    return bool;
  }

  public async save() {
    if (!this.checkFileSize()) {
      return;
    }
    let finance_expect_finance_date = '';
    if (this.year && this.month && this.day) {
      finance_expect_finance_date = this.year.toString() + '-' + this.month.toString() + '-' + this.day.toString();
    }
    if (this.finance_use_of_funds.includes('その他')) {
      let index = this.finance_use_of_funds.indexOf('その他');
      if (this.use_of_fund_other) {
        this.finance_use_of_funds[index] = this.use_of_fund_other;
      } else {
        this.finance_use_of_funds.splice(index, 1);
      }
    }
    const finance: IFinance = {
      finance_expect_finance_date: finance_expect_finance_date,
      finance_expect_finance_amount: (this.finance_expect_finance_amount) ? this.finance_expect_finance_amount * thousand : null,
      finance_expect_finance_method: this.finance_expect_finance_method,
      finance_expect_valuation_pre: (this.finance_expect_valuation_pre) ? this.finance_expect_valuation_pre * thousand : null,
      finance_achievement: this.finance_achievement,
      finance_use_of_funds: this.finance_use_of_funds,
      finance_materials: this.materials,
      user_need_consultation: this.user_need_consultation,
    };
    const setup_finance: ISetUpFinance = {
      finance: finance,
      upload_files: this.finance_materials,
      delete_file_ids: this.deleteFileIds,
    }

    await dispatchSetUpFinance(this.$store, setup_finance);
    commitAddNotification(this.$store, { content: '保存されました', condition: 'saved' });
    this.reset();
  }

  public async submit() {
    if (!this.checkFileSize()) {
      return;
    }
    if (!this.isClick || await this.$validator.validateAll()) {
      let finance_expect_finance_date = '';
      if (this.year && this.month && this.day) {
        finance_expect_finance_date = this.year.toString() + '-' + this.month.toString() + '-' + this.day.toString();
      }
      if (this.finance_use_of_funds.includes('その他')) {
        let index = this.finance_use_of_funds.indexOf('その他');
        this.finance_use_of_funds[index] = this.use_of_fund_other;
      }
      const finance: IFinance = {
        finance_expect_finance_date: finance_expect_finance_date,
        finance_expect_finance_amount: (this.finance_expect_finance_amount) ? this.finance_expect_finance_amount * thousand : null,
        finance_expect_finance_method: this.finance_expect_finance_method,
        finance_expect_valuation_pre: (this.finance_expect_valuation_pre) ? this.finance_expect_valuation_pre * thousand : null,
        finance_achievement: this.finance_achievement,
        finance_use_of_funds: this.finance_use_of_funds,
        finance_materials: this.materials,
        user_need_consultation: this.user_need_consultation,
      };
      const setup_finance: ISetUpFinance = {
        finance: finance,
        upload_files: this.finance_materials,
        delete_file_ids: this.deleteFileIds,
      }
      await dispatchSetUpFinance(this.$store, setup_finance);
      await dispatchSetupComplete(this.$store);
      this.$router.push('/setup/complete');
    }
  }

  public resetDay() {
    this.day = 0;
  }

  public years() {
    const years: number[] = [];
    for (let year = new Date().getFullYear(); year <= (new Date().getFullYear() + 20); year++) {
      years.push(year);
    }
    return years;
  }

  public months() {
    const months = [...Array(12)].map((ele, i) => i + 1);
    return months;
  }

  public days() {
    const days: number[] = [];
    const date = (this.year && this.month) ? new Date(this.year, this.month, 0) : new Date();
    for (let day = 1; day <= date.getDate(); day++) {
      days.push(day);
    }
    return days
  }

  get isComplete() {
    return this.user_need_consultation != null
      && (!this.isClick ||
        (this.year && this.month && this.day
          && this.finance_expect_finance_amount
          && this.finance_use_of_funds.length > 0));
  }
}
